<template>
  <div>
    <v-combobox
      v-model="scenarioElements"
      ref="combobox"
      :items="availableElements"
      :item-text="showLogicalLanguage ? 'prolog' : 'name'"
      item-value="id"
      hide-no-data
      hide-selected
      :label="$t('scenario.elements')"
      multiple
      allow-overflow
      :clearable="mode !== 2"
      outlined
      :search-input.sync="search"
      @change="search=null;addElementList($event)"
      @input="$emit('completed')"
      @blur="$emit('completed')"
      @keydown="$emit('notCompleted')"
      :item-disabled="isItemDisabled"
    >
      <template v-slot:selection="{ parent, item, index }">
        <v-text-field
          v-if="indexElementToUpdate === index"
          v-model="elementEditValue"
          flat
          autofocus
          :filled="false"
          background-color="grey lighten-4"
          dense
          hide-details
          solo
          @keyup.enter="editElement()"
          @click:append-outer="editElement()"
          append-outer-icon="mdi-check"
          @blur="cancelUpdateElement()"
        >
        </v-text-field>

        <element-chip
          v-else
          :element="convertStringToElement(item)"
          :close="allowDelete || (mode === 2 && selectedElements.find(e => e.id === item.id) === undefined)"
          :close-function="parent.selectItem"
          :close-function-parameters="[item]"
          :edit="allowEdit || (mode === 2 && newElementsList.find(e => e.id === item.id) !== undefined)"
          :edit-function="openEditElement"
          :edit-function-parameters="[item, index]"
        />
      </template>

      <template v-slot:item="data">
        <v-list-item-action>
          <v-icon v-if="data.attrs.inputValue">mdi-checkbox-marked</v-icon>
          <v-icon v-else>mdi-checkbox-blank-outline</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>
            {{
              showLogicalLanguage
                ? data.item.prolog ||
                  $t("global.notranslation", { something: data.item.name })
                : data.item.name ||
                  $t("global.notranslation", { something: data.item.prolog })
            }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action @click.stop v-if="allowDelete || (mode === 2 && newElementsList.find(e => e.id === data.item.id) !== undefined)">
          <v-btn
            icon
            @click.stop.prevent="confirmDeleteElementDialog(data.item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-list-item-action>
      </template>
    </v-combobox>

    <template v-if="!createNewScenario">
      <v-card-actions>
        <div class="ma-auto">
          <v-btn
            color="primary"
            tile
            @click="confirmScenarioEdition"
            class="mx-1"
            :disabled="!isScenarioValidationAllowed"
          >
            {{ $t("scenario.confirmEditScenario") }}
          </v-btn>
          <v-btn
            color="ternary"
            dark
            tile
            @click="cancelScenarioEdition"
            class="mx-1"
          >
            {{ $t("global.cancel") }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <template v-else>
      <v-card-actions v-if="showValidate">
        <div class="ma-auto">
          <raison-button
            :button-label="$t('global.validate')"
            :button-function="createScenario"
            :loading="loading || callingApi"
            :disabled="!isScenarioValidationAllowed || loading || callingApi"
          >
            <template v-slot:append>
              <v-icon right>mdi-arrow-right</v-icon>
            </template>
          </raison-button>
        </div>
      </v-card-actions>
    </template>

    <v-dialog v-model="confirmElementDeletion" max-width="490">
      <v-card>
        <v-card-title class="headline">
          <span class="ise-modal-delete-title">
            {{ $t("scenario.doYouConfirmTheDeletionOf") }}
          </span>
          <element-chip :element="this.elementToDelete" /> 
          ?
        </v-card-title>
        <v-card-text>{{
          $t("scenario.confirmElementDeletionDescription")
        }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmElementDeletion = false">
            {{ $t("global.no") }}
          </v-btn>
          <v-btn
            text
            :loading="isDeletingElement"
            @click="deleteElement(elementToDelete)"
            color="blue darken-1"
          >
            {{ $t("global.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ElementChip from "@/components/global/ElementChip";
import RaisonButton from "@/components/global/RaisonButton";

import elementService from "@/services/elements.js";
import scenarioService from "@/services/scenarios.js";

export default {
  name: "InputScenarioElements",

  components: { ElementChip, RaisonButton },

  data() {
    return {
      scenarioElements: [],
      indexElementToUpdate: -1,
      elementEditValue: "",
      ignoreFirstBlur: true,
      confirmElementDeletion: false,
      elementToDelete: {
        id: "",
        name: "",
      },
      isDeletingElement: false,
      projectId: this.$route.params.id,
      newElementsList: [],
      loading: false,
      search: null,
    };
  },

  props: {
    refresh: {
      type: Boolean,
      required: false,
      default: false
    },
    availableElements: {
      type: Array,
      required: true,
    },
    updateAvailableElements: {
      type: Function,
      required: true,
    },
    updateAvailableScenarios: {
      type: Function,
      required: false,
      default: () => {},
    },
    createNewScenario: {
      type: Boolean,
      required: true,
    },
    validateScenarii: {
      type: Function,
      required: false,
      default: () => {},
    },
    cancelScenarioEdition: {
      type: Function,
      required: false,
      default: () => {},
    },
    successfulScenarioUpdate: {
      type: Function,
      required: false,
      default: () => {},
    },
    selectedElements: {
      type: Array,
      required: false,
      default: () => [],
    },
    callingApi: {
      type: Boolean,
      required: false,
      default: false,
    },
    scenario: {
      type: Object,
      required: false,
    },
    showValidate: {
      type: Boolean,
      required: false,
      default: true,
    },
    addElementList: {
      type: Function,
      required: false,
      default: () => {},
    },
    allowEdit: {
      type: Boolean,
      required: false,
      default: true
    },
    allowDelete: {
      type: Boolean,
      required: false,
      default: true
    },
    mode: {
      type: Number,
      required: false,
      default: 1 // set to 2 to allow modifications and delete only on new elements
    }
  },

  computed: {
    showLogicalLanguage() {
      return this.$store.state.showLogicalLangage;
    },

    isScenarioValidationAllowed() {
      return this.scenarioElements.length > 0;
    },
  },

  watch: {
    scenarioElements(current, prev) {
      if (current.find(c => c.id === undefined) === undefined) {
        this.$emit('update-elements', current)
      }
      if (prev.length === current.length) return;
      this.scenarioElements = current.map(element => {
        if(typeof element === 'string') {
          this.$refs.combobox.isMenuActive = false
          let existingElement = this.findExistingElement(element);
          if(existingElement) {
            return existingElement;
          }
          let newElement = this.convertStringToElement(element);
          elementService
            .createElement(this.$route.params.id, newElement)
            .then((res) => {
              let index = this.scenarioElements.findIndex(it => it === element);
              this.scenarioElements.splice(index, 1, res.data);
              this.newElementsList.splice(this.newElementsList.length, 0, res.data);
              this.updateAvailableElements([res.data, ...this.availableElements]);
            })
            .catch((err) => {
              this.$emit("raison-event", { type:"error", messages: [err] });
            });
        }
        return element;
      });
    },
  },

  methods: {
    convertStringToElement(elementValue) {
      if (typeof elementValue === "string") {
        return {
          name: this.showLogicalLanguage ? "" : elementValue,
          prolog: this.showLogicalLanguage ? elementValue : "",
        };
      }
      return elementValue;
    },

    openEditElement(element, index) {
      this.elementEditValue = this.showLogicalLanguage
        ? element.prolog
        : element.name;
      this.$refs.combobox.blur();
      this.indexElementToUpdate = index;
      this.ignoreFirstBlur = true;
    },

    editElement() {
      let elementToUpdate = this.scenarioElements[this.indexElementToUpdate];
      let inputValue = JSON.parse(JSON.stringify(this.elementEditValue));

      if (this.showLogicalLanguage) {
        elementToUpdate.prolog = inputValue;
      } else {
        elementToUpdate.name = inputValue;
      }

      elementService
        .updateElement(this.$route.params.id, elementToUpdate)
        .then((res) => {
          let index = this.scenarioElements.findIndex(it => it === elementToUpdate);
          this.scenarioElements.splice(index, 1, res.data);
          this.updateAvailableElements([res.data, ...this.availableElements]);
        })
        .catch((err) => {
          this.$emit("raison-event", { type: "error", messages: [err] });
        })
        .finally(() => {
          this.indexElementToUpdate = -1;
          this.ignoreFirstBlur = true;
        });
    },

    cancelUpdateElement() {
      if (!this.ignoreFirstBlur) {
        this.indexElementToUpdate = -1;
      } else {
        this.ignoreFirstBlur = false;
      }
    },

    createScenario() {
      if (this.isScenarioValidationAllowed) {
        this.loading = true;
        this.validateScenarii(this.scenarioElements);
        this.scenarioElements = []
        this.loading = false;
      }
    },

    confirmScenarioEdition() {
      let scenarioToUpdate = this.scenario;
      scenarioToUpdate.elements = this.scenarioElements;

      this.prepareScenarioRequest(scenarioToUpdate);

      scenarioService.updateScenario(this.$route.params.id, scenarioToUpdate)
        .then((res) => {
          this.updateAvailableScenarios(res.data);
        })
        .finally(() => {
          this.successfulScenarioUpdate();
        });
    },

    //replace element object by simple object which contains only element ID
    prepareScenarioRequest(scenario) {
      let elements = scenario.elements;
      scenario.elements = [];
      elements.forEach((elementItem) => {
        scenario.elements.push({ elementId: elementItem.id, name: "" }); // TODO the name field will no be mandatory
      });
    },

    findElementByName(elementName) {
      return this.availableElements.find(
        (elementItem) => elementItem.name === elementName
      );
    },

    findElementByProlog(elementProlog) {
      return this.availableElements.find(
        (elementItem) => elementItem.prolog === elementProlog
      );
    },

    findExistingElement(elementValue) {
      if (this.showLogicalLanguage) {
        return this.findElementByProlog(elementValue);
      } else {
        return this.findElementByName(elementValue);
      }
    },

    confirmDeleteElementDialog(element) {
      this.confirmElementDeletion = true;
      this.elementToDelete = element;
    },

    deleteElement(element) {
      this.isDeletingElement = true;
      elementService
        .deleteElement(this.projectId, element.id)
        .then(() => {
          let elements = this.availableElements.filter(elt => elt.id !== element.id);
          // ensure to return a list even the filter return only one element
          elements = [].concat(elements.length ? elements : []);
          this.updateAvailableElements(elements);
          const index = this.scenarioElements.map(e => e.id).indexOf(element.id)
          if (index !== -1) {
            this.scenarioElements.splice(index, 1)
          }
          this.$emit("raison-event", {type: "success", messages: [
            this.$t("scenario.elementDeleted", {
              element: this.showLogicalLanguage ? element.prolog : element.name,
            }),
          ]})
        })
        .catch((err) => {
          this.$emit("raison-event", { type: "error", messages: [ err ] });
        }).finally(() => {
          this.isDeletingElement = false;
          this.confirmElementDeletion = false;
        });
    },

    isItemDisabled (item) {
      if (this.mode !== 2) return false;
      return this.selectedElements.find(e => e.id === item.id) !== undefined
    }
  },

  created() {
    this.scenarioElements = this.selectedElements;
  },

};
</script>
<style scoped>
  .ise-modal-delete-title {
    margin-right: 5px;
  }
</style>